import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Autocomplete,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Close,
  Add,
  ExpandMore,
  Edit,
  Remove,
  GitHub,
  ArrowRight,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setProfileState } from "../../../actions/MyTechProfile/myTechProfileActions";
import "./EditMyTechProfileProjectDetails.css";
import { allSkills } from "../../../utils/constants";

const initialFormState = {
  title: "",
  image: "",
  details: "",
  highlights: [{ id: `highlight-${Date.now()}`, title: "" }],
  techStack: [],
  githubLink: "",
};

const EditMyTechProfileProjectDetails = ({ scrollTo }) => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.editMyTechProfileStore);

  const [projects, setProjects] = useState(profile?.projects || []);
  const [isAdding, setIsAdding] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formState, setFormState] = useState(initialFormState);

  useEffect(() => {
    if (profile) {
      setProjects(profile.projects || []);
    }
  }, [profile]);

  useEffect(() => {
    if (editingProject) {
      const highlights =
        editingProject.highlights && editingProject.highlights.length > 0
          ? editingProject.highlights
          : [{ id: `highlight-${Date.now()}`, title: "" }];

      setFormState({ ...editingProject, highlights });
      setIsAdding(false);
    } else {
      setFormState(initialFormState);
    }
  }, [editingProject]);

  const handleProjectRemove = (projectId) => {
    const updatedProjects = projects.filter((p) => p.id !== projectId);
    setProjects(updatedProjects);
    dispatch(setProfileState({ ...profile, projects: updatedProjects }));
  };

  const handleEditClick = (e, project) => {
    e.stopPropagation();
    setEditingProject(project);
    scrollTo("projects");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormState({ ...formState, [name]: value });
  };

  const handleHighlightChange = (id, value) => {
    const updatedHighlights = formState.highlights.map((h) =>
      h.id === id ? { ...h, title: value } : h
    );
    setFormState({ ...formState, highlights: updatedHighlights });
  };

  const addHighlightField = () => {
    setFormState({
      ...formState,
      highlights: [
        ...formState.highlights,
        { id: `highlight-${Date.now()}`, title: "" },
      ],
    });
  };

  const removeHighlightField = (id) => {
    const updatedHighlights = formState.highlights.filter((h) => h.id !== id);
    setFormState({ ...formState, highlights: updatedHighlights });
  };

  const handleTechStackChange = (event, value) => {
    setFormState({ ...formState, techStack: value });
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingProject(null);
    setFormState(initialFormState);
  };

  const handleSave = () => {
    const finalFormState = {
      ...formState,
      highlights: formState.highlights.filter((h) => h.title.trim() !== ""),
    };

    if (editingProject) {
      const updatedProjects = projects.map((p) =>
        p.id === editingProject.id ? finalFormState : p
      );
      setProjects(updatedProjects);
      dispatch(setProfileState({ ...profile, projects: updatedProjects }));
    } else if (isAdding && finalFormState.title) {
      const projectToAdd = { id: Date.now(), ...finalFormState };
      const updatedProjects = [projectToAdd, ...projects];
      setProjects(updatedProjects);
      dispatch(setProfileState({ ...profile, projects: updatedProjects }));
    }
    handleCancel();
  };

  const showForm = isAdding || editingProject;

  return (
    <Box>
      <div className="editmytechprofile-projects-list">
        {projects.map((project) => (
          <Accordion
            key={project.id}
            className="editmytechprofile-accordion-nested"
            onChange={() => scrollTo("projects")}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleProjectRemove(project?.id);
                }}
              >
                <Close fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => handleEditClick(e, project)}
              >
                <Edit fontSize="small" />
              </IconButton>
              <Typography sx={{ ml: 1, flexShrink: 0 }}>
                {project?.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <img
                src={project?.image}
                alt={project?.title}
                className="editmytechprofile-project-image"
              />
              <Typography variant="h6" gutterBottom>
                {project?.title}
              </Typography>
              <Typography variant="body2" paragraph>
                {project?.details}
              </Typography>

              {project.highlights && project.highlights.length > 0 && (
                <>
                  <Typography variant="subtitle1" gutterBottom>
                    Features
                  </Typography>
                  <List dense>
                    {project.highlights.map((highlight) => (
                      <ListItem key={highlight.id} sx={{ pl: 0 }}>
                        <ListItemIcon sx={{ minWidth: "auto", mr: 1 }}>
                          <ArrowRight fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={highlight.title} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {project.githubLink && (
                <Button
                  variant="contained"
                  startIcon={<GitHub />}
                  href={project.githubLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    color: "black",
                    backgroundColor: "#ffd700 ",
                    "&:hover": {
                      backgroundColor: "#ffd700",
                    },
                  }}
                >
                  View on GitHub
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

      {!showForm ? (
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            scrollTo("projects");
            setIsAdding(true);
            setEditingProject(null);
            setFormState(initialFormState);
          }}
          fullWidth
          className="editmytechprofile-add-button"
        >
          Add Project
        </Button>
      ) : (
        <Box className="editmytechprofile-add-project-form">
          <Typography variant="h6" gutterBottom>
            {editingProject ? "Edit Project" : "Add New Project"}
          </Typography>
          <TextField
            label="Image URL"
            name="image"
            value={formState.image}
            onChange={handleFormChange}
            fullWidth
            size="small"
            className="editmytechprofile-input-spacing"
          />
          <TextField
            label="Title"
            name="title"
            value={formState.title}
            onChange={handleFormChange}
            fullWidth
            size="small"
            className="editmytechprofile-input-spacing"
          />
          <TextField
            label="Details"
            name="details"
            value={formState.details}
            onChange={handleFormChange}
            fullWidth
            size="small"
            multiline
            rows={3}
            className="editmytechprofile-input-spacing"
          />

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Features/Highlights
          </Typography>
          {formState.highlights.map((highlight, index) => (
            <Box key={highlight.id} className="highlight-field-container">
              <TextField
                label={`Highlight ${index + 1}`}
                value={highlight.title}
                onChange={(e) =>
                  handleHighlightChange(highlight.id, e.target.value)
                }
                fullWidth
                size="small"
              />
              {formState.highlights.length > 1 && (
                <IconButton
                  onClick={() => removeHighlightField(highlight.id)}
                  size="small"
                >
                  <Remove />
                </IconButton>
              )}
            </Box>
          ))}
          <Button
            startIcon={<Add />}
            onClick={addHighlightField}
            fullWidth
            className="editmytechprofile-add-highlight-button"
            sx={{
              border: "1px solid #ffd700",
              color: "#ffd700",
              fontFamily: "Questrial",
              fontSize: "14px",
              textTransform: "inherit",
              "&:hover": {
                border: "1px solid #ffd700",
                backgroundColor: "#ffd90009",
              },
            }}
          >
            Add More Features/Highlights
          </Button>

          <Autocomplete
            multiple
            options={allSkills}
            getOptionLabel={(option) => option.name}
            value={formState.techStack}
            onChange={handleTechStackChange}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option.name}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Tech Stack"
                size="small"
              />
            )}
            className="editmytechprofile-input-spacing"
            sx={{ mt: 2 }}
          />
          <TextField
            label="GitHub Link"
            name="githubLink"
            value={formState.githubLink}
            onChange={handleFormChange}
            fullWidth
            size="small"
            className="editmytechprofile-input-spacing"
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Button
              variant="text"
              onClick={handleCancel}
              className="editmytechprofile-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              size="small"
              sx={{ ml: 1 }}
              className="editmytechprofile-save-button"
            >
              Save
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default EditMyTechProfileProjectDetails;
